import React from 'react';
import { connect } from 'react-redux';
import { User } from 'oidc-client';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '../../store';
import { Row, Col, Card, Spinner, Tabs, Tab, Button, Form, InputGroup, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router';
import * as DashboardStore from '../../store/Dashboard';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpandAlt, faFileClipboard, faPencil, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import EditStaleHoursModal from '../Report/Modals/EditStaleHoursModal';
import DatasetRefreshSchedule from '../Report/DatasetRefreshSchedule';
import DashboardAliasesTable from './DashboardAliasTable';
import DatasetRefreshHistoryTable from '../Report/DatasetRefreshHistoryTable';
import CopyableSystemId from '../../components/copyableSystemId';
import { userHasAnyRole } from '@konicaminolta/common-react';
import { ROLE_FULL_ADMINISTRATOR } from '../../appRoles';
import DashboardView from './DashboardView';
import RecordAccessControlTable from '../Common/RecordAccessControl/RecordAccessControlTable';
import { PowerBiObjectType } from '../../types/Common/PowerBiObjectType';
import NoPermissions from '../Common/NoPermissions/NoPermissions';


enum DashboardDetailsTabs {
    Dashboard = 'dashboard',
    Aliases = 'aliases',
    Permissions = 'permission',
    RefreshSchedule = 'refreshSchedule',
    RefreshHistory = 'refreshHistory'
}

type DashboardDetailsProps = {
    dispatch: Dispatch;
    dashboardState: DashboardStore.DashboardState;
    user: User;
    actions: typeof DashboardStore.actionCreators;
}
& RouteComponentProps<{dashboardId: string}>;

interface DashboardDetailsState {
    tabKey: DashboardDetailsTabs | DashboardDetailsTabs.Dashboard;
    showUpdateHoursUntilStaleModal: boolean;
    isLoading: boolean;
}

class DashboardDetails extends React.Component<DashboardDetailsProps, DashboardDetailsState> {
    private dashboardViewChildRef: React.RefObject<DashboardView>;
    public constructor(props: DashboardDetailsProps) {
        super(props);
        this.state = {
            tabKey: DashboardDetailsTabs.Dashboard,
            showUpdateHoursUntilStaleModal: false,
            isLoading: true,
        };
        this.dashboardViewChildRef = React.createRef();
    }

    public componentDidMount() {
        this.props.actions.unloadDashboardDetails();
        this.ensureDataFetched();
    }

    public componentDidUpdate() {
        this.ensureDataFetched();
    }

    private ensureDataFetched() {
        this.props.actions.requestDashboardDetails(this.props.match.params.dashboardId);
        
        if (this.state.isLoading && !this.props.dashboardState.isLoadingDetails && this.props.dashboardState.isLoadedDetails) {
            this.setState({isLoading: false});
        }
    }

    private refreshDashboardDetails() {
        this.setState({isLoading: true});
        this.componentDidMount();
    }

    private handleUpdateStaleHoursModal() {
        // Refresh Dashboard Details
        this.refreshDashboardDetails();
        
        this.ensureDataFetched();
        this.handleCloseEditStaleHoursModal();
    }

    private handleOpenEditStaleHoursModal() {
        if(userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR])){
            this.setState({ showUpdateHoursUntilStaleModal: true });
        }
    }
    private handleCloseEditStaleHoursModal() {
        this.setState({ showUpdateHoursUntilStaleModal: false });
    }

    // Handle the NavMenu click for full screen and pass on the request to the child component
    onFullScreenClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.dashboardViewChildRef?.current?.powerBiEmbedFullScreen();
    };

    public render() {
        let dashboard = this.props.dashboardState.data.find(x => x.id === this.props.match.params.dashboardId);
        if(this.props.dashboardState.hasPermissionsToDetails && this.props.dashboardState.isLoadedDetails && dashboard === undefined) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            );
        }

        if (!this.props.dashboardState.hasPermissionsToDetails) {
            return (
                <NoPermissions />
            );
        }

        if(dashboard === undefined){
            return (
                <div></div>
            );
        }

        let aliasName = dashboard?.aliasName ? dashboard?.aliasName : dashboard?.id;
        const dashboardShareUrl  = `${window.location.origin}/powerbireportviewer/dashboards/viewdashboard/${aliasName}`;
        
        return (
            <React.Fragment>
                <div id="detailsContainer">
                <Row>
                    <Col>
                        <Card className="panel">
                            <Card.Header>
                                <Row>
                                    <Col md={12}>
                                        <h3>Dashboard Details</h3>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body>
                            <Row>
                                <Col lg={3} className="mr-1">
                                    <Card>
                                        <Card.Body>
                                            <Row>
                                                <Col
                                                    className="text-center"
                                                    xs={12}
                                                >
                                                    <h2>
                                                        {dashboard?.displayName}
                                                    </h2>
                                                </Col>
                                            </Row>
                                            <Row className="mt-1">
                                                <small>
                                                    <Col className="d-flex flex-column align-items-center">
                                                        Dashboard ID: {dashboard?.id ? dashboard?.id : ""}
                                                    </Col>
                                                </small>
                                            </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end">
                                                        <strong>
                                                            Group:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {
                                                            dashboard?.groupName
                                                        }
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end md-6">
                                                        <strong>
                                                            Dashboard Is Stale:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {dashboard?.isStale === true ? "Yes" : "No" }
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end md-6">
                                                        <strong>
                                                            Last Data Refresh Date:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {dashboard?.lastDatasetRefreshHistoryDate ? format(new Date(dashboard.lastDatasetRefreshHistoryDate), 'yyyy-MM-dd hh:mm:ss')  : ''}
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end">
                                                        <strong>
                                                            Hours until stale:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        <Row>
                                                            <Col>
                                                                {dashboard?.hoursUntilStale ? dashboard.hoursUntilStale : ''}
                                                            </Col>
                                                            <Col>
                                                                {
                                                                    userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR]) &&
                                                                    <div onClick={() => this.handleOpenEditStaleHoursModal()}><FontAwesomeIcon icon={faPencil} /></div>
                                                                }
                                                                
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col xs={6} className="text-end">
                                                        <strong>
                                                            Tenant:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                    <div className="text-break">
                                                        <CopyableSystemId
                                                        data={
                                                            dashboard?.tenantDomain || ''
                                                        }
                                                        />
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col xs={6} className="text-end">
                                                        <strong>
                                                            Tenant Id:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        <div className="text-break">
                                                        <CopyableSystemId
                                                        data={
                                                            dashboard?.tenantId || ''
                                                        }
                                                        />
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <br />
                                                <Row className="mt-4">
                                                    <strong>Dashboard Share URL</strong>
                                                </Row>
                                                <Row>
                                                    <InputGroup className="mb-3">
                                                    <Form.Control
                                                            aria-label="Dashboard Share URL"
                                                            aria-describedby='dashboardShareUrl'
                                                            value={dashboardShareUrl}
                                                            readOnly
                                                    />
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Copy dashboard share URL to clipboard</span></Tooltip>}
                                                    >
                                                        <Button className="btn btn-info" onClick={() => navigator.clipboard.writeText(dashboardShareUrl)}><FontAwesomeIcon icon={faFileClipboard} /></Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Open dashboard share URL in a new tab</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => window.open(dashboardShareUrl, '_blank', 'noopener,noreferrer')}><FontAwesomeIcon icon={faUpRightFromSquare} /></Button>
                                                    </OverlayTrigger>
                                                    </InputGroup>
                                                </Row>
                                                <Row className="mt-4">
                                                    <strong>Dashboard URL</strong>
                                                </Row>
                                                <Row>
                                                    <InputGroup className="mb-3">
                                                    <Form.Control
                                                            aria-label="Dashboard URL"
                                                            aria-describedby='dashboardUrl'
                                                            value={dashboard?.powerBiUrl}
                                                            readOnly
                                                    />
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Copy dashboard URL to clipboard</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => navigator.clipboard.writeText(dashboard?.powerBiUrl ?? "")}><FontAwesomeIcon icon={faFileClipboard} /></Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Open dashboard URL in a new tab</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => window.open(dashboard?.powerBiUrl ?? "", '_blank', 'noopener,noreferrer')}><FontAwesomeIcon icon={faUpRightFromSquare} /></Button>
                                                    </OverlayTrigger>
                                                    </InputGroup>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={9}>
                                        {dashboard?.id === this.props.match.params.dashboardId && (
                                            <Tabs
                                                activeKey={this.state.tabKey}
                                                onSelect={(x: string | null) =>
                                                    this.setState({ tabKey: x as DashboardDetailsTabs })
                                                }
                                                className="mb-4"
                                                defaultActiveKey={
                                                    DashboardDetailsTabs.Dashboard
                                                }
                                                id="tab-details"
                                            >
                                                <Tab
                                                    eventKey={
                                                        DashboardDetailsTabs.Dashboard
                                                    }
                                                    title="Dashboard"
                                                >
                                                    <Nav className="justify-content-end">
                                                        <Nav.Item id="report-fullscreen" role="button" onClick={this.onFullScreenClick}>
                                                            <div style={{color: 'black', verticalAlign: 'top', paddingBottom:'4px'}}>
                                                            <span><FontAwesomeIcon className="me-2 text-dark" icon={faExpandAlt} />Full Screen</span>
                                                            </div>
                                                        </Nav.Item>
                                                    </Nav>
                                                    <DashboardView
                                                        ref={this.dashboardViewChildRef}
                                                        dashboardId={dashboard.id}
                                                        {...this.props}
                                                    />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        DashboardDetailsTabs.Permissions
                                                    }
                                                    title="Permissions"
                                                >
                                                    <RecordAccessControlTable
                                                     {...this.props}
                                                     record={dashboard}
                                                     recordType={PowerBiObjectType.Dashboard}
                                                     />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        DashboardDetailsTabs.Aliases
                                                    }
                                                    title="Aliases"
                                                >
                                                    <DashboardAliasesTable
                                                     {...this.props}
                                                     dashboardId={dashboard.id}
                                                     refreshDashboard={() => this.refreshDashboardDetails()}
                                                     />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        DashboardDetailsTabs.RefreshSchedule
                                                    }
                                                    title="Refresh Schedule"
                                                >
                                                <DatasetRefreshSchedule
                                                    datasetId={dashboard.datasetId}
                                                    {...this.props}
                                                />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        DashboardDetailsTabs.RefreshHistory
                                                    }
                                                    title="Refresh History"
                                                >
                                                <DatasetRefreshHistoryTable
                                                    datasetId={dashboard.datasetId}
                                                    {...this.props}
                                                />
                                                </Tab>
                                            </Tabs>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                <React.Fragment>
                    <EditStaleHoursModal
                        dispatch={this.props.dispatch}
                        powerBiObject={dashboard}
                        updateHoursUntilStale={() => this.handleUpdateStaleHoursModal()}
                        hideModal={() => this.handleCloseEditStaleHoursModal()}
                        showModal={this.state.showUpdateHoursUntilStaleModal}
                    />
                    </React.Fragment>
                }
                </div>
            </React.Fragment>

        );
    }
}

function mapStateToProps(state: AppState) {
    return {
        dashboardState: state.dashboard,
        user: state.oidc.user,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => {
        return {
            dispatch: dispatch,
            actions: bindActionCreators(Object.assign(
                {},
                DashboardStore.actionCreators
            ), dispatch )
        }
    },
)(DashboardDetails as any);

